/* ===================================================================
   Workbench client types — the v2 agent record mirror plus the pure
   helpers the builder needs synchronously (condition grouping, token
   extraction, dependency/stage derivation). The authoritative shapes
   and vocabulary live server-side in api/src/services/agentSchema.ts;
   the vocabulary (signals, inputs, token map) is FETCHED from
   /agent-bundles/workbench-meta so it cannot drift.
=================================================================== */

export interface AgentCondition {
  j: "and" | "or";
  t: "det" | "sem";
  c: string;
  neg?: boolean;
}

export interface TrayDestCfg {
  kind: "decision" | "choice" | "input" | "notice";
  gate: boolean;
  holdField?: string;
}

export interface TrackerDestCfg {
  key: string;
  resultKey: string;
  matchOn: string[];
}

export interface AgentDestinations {
  tray?: TrayDestCfg;
  sa?: boolean;
  saSections?: string[];
  memo?: boolean;
  tracker?: TrackerDestCfg;
  trackers?: TrackerDestCfg[];
  stateOfPlay?: boolean;
}

export interface AgentModelProfile {
  instructions?: string;
  tau?: number;
  maxTokens?: number;
}

export interface AgentDefV2 {
  v: 2;
  name: string;
  desc: string;
  role: "agent" | "cleaner" | "router";
  enabled: boolean;
  cond: AgentCondition[];
  inputs: string[];
  prompt: string;
  returns: string;
  strictSections?: string[];
  sectionThresholds?: Record<string, number>;
  allowPartialSections?: boolean;
  partialSectionPrerequisite?: string;
  dest: AgentDestinations;
  model: string;
  tau: number;
  maxTokens: number;
  modelProfiles?: Record<string, AgentModelProfile>;
}

export type BundleMapV2 = Record<string, AgentDefV2>;

export interface SignalMeta {
  id: string;
  label: string;
  group: string;
  providers: string[];
}

export interface WorkbenchMeta {
  signals: SignalMeta[];
  builtinInputs: Array<{ id: string; label: string }>;
  tokenRequirements: Record<string, string>;
  spineImpliedDeps: string[];
  seedAgents: BundleMapV2;
}

export interface BundleDetailV2 {
  id: string;
  name: string;
  note: string | null;
  isArchived: boolean;
  draft: BundleMapV2;
  createdAt: string;
  updatedAt: string;
  versions: Array<{
    id: string;
    bundleId: string;
    label: string;
    note: string | null;
    snap: BundleMapV2;
    createdAt: string;
  }>;
}

/* ---- pure helpers (mirror agentSchema semantics) ------------------------- */

/** OR-groups with AND binding tighter: rows joined "or" start a new group. */
export function conditionGroups(cond: AgentCondition[]): AgentCondition[][] {
  const groups: AgentCondition[][] = [];
  for (const row of cond || []) {
    if (!groups.length || row.j === "or") groups.push([row]);
    else groups[groups.length - 1].push(row);
  }
  return groups;
}

export function conditionSummary(cond: AgentCondition[], label: (id: string) => string): string {
  if (!cond || !cond.length) return "Every email";
  const rowText = (r: AgentCondition): string => {
    const base = r.t === "det" ? label(r.c) : `“${r.c}”`;
    return r.neg ? `not ${base}` : base;
  };
  return conditionGroups(cond)
    .map((g) => g.map(rowText).join(" and "))
    .join(", or ");
}

export function promptTokens(prompt: string): string[] {
  const out = new Set<string>();
  for (const m of String(prompt || "").matchAll(/\{([a-z_]+)\}/g)) out.add(m[1]);
  return [...out];
}

export function unboundTokens(def: Pick<AgentDefV2, "prompt" | "inputs">, meta: WorkbenchMeta): string[] {
  const inputs = new Set(def.inputs || []);
  const hasAgentRef = [...inputs].some((i) => i.startsWith("agent:"));
  return promptTokens(def.prompt).filter((t) => {
    const req = meta.tokenRequirements[t];
    if (req === undefined) return true;
    if (req === "") return !hasAgentRef;
    return !inputs.has(req);
  });
}

export function promptForModel(def: AgentDefV2, model = def.model): string {
  const instructions = def.modelProfiles?.[model]?.instructions?.trim();
  if (!instructions) return def.prompt;
  const marker = "\n\nUSER:\n";
  const at = def.prompt.indexOf(marker);
  const profile = `\n\nMODEL-SPECIFIC CALIBRATION (${model}):\n${instructions}`;
  return at >= 0
    ? `${def.prompt.slice(0, at)}${profile}${def.prompt.slice(at)}`
    : `${def.prompt}${profile}`;
}

export function tauForModel(def: AgentDefV2, model = def.model): number {
  return def.modelProfiles?.[model]?.tau ?? def.tau;
}

export function maxTokensForModel(def: AgentDefV2, model = def.model): number {
  return def.modelProfiles?.[model]?.maxTokens ?? def.maxTokens;
}

export function setBundleModel(bundle: BundleMapV2, model: string): BundleMapV2 {
  return Object.fromEntries(Object.entries(bundle).map(([id, def]) => [id, { ...def, model }]));
}

/** Direct dependencies inside this bundle — mirrors executor.agentDeps. */
export function agentDeps(id: string, def: AgentDefV2, bundle: BundleMapV2, meta: WorkbenchMeta): string[] {
  const deps = new Set<string>();
  const present = (a: string): boolean => a !== id && !!bundle[a] && bundle[a].enabled;
  for (const input of def.inputs || []) {
    if (input.startsWith("agent:")) {
      const dep = input.slice(6);
      if (present(dep)) deps.add(dep);
    } else if (input === "spine") {
      for (const dep of meta.spineImpliedDeps) if (present(dep)) deps.add(dep);
    } else if (input === "trackers" && !trackerDestinations(def.dest).length) {
      for (const [aid, a] of Object.entries(bundle))
        if (a.enabled && trackerDestinations(a.dest).length && present(aid)) deps.add(aid);
    }
  }
  const signalById = new Map(meta.signals.map((s) => [s.id, s]));
  for (const row of def.cond || []) {
    if (row.t !== "det") continue;
    for (const p of signalById.get(row.c)?.providers || []) if (present(p)) deps.add(p);
  }
  return [...deps];
}

export interface Stage {
  title: string;
  ids: string[];
}

/** Stages derived from dependencies: cleaner, router, then topological levels
    of everything else (disabled agents shown in their last-known level). */
export function deriveStages(bundle: BundleMapV2, meta: WorkbenchMeta): Stage[] {
  const ids = Object.keys(bundle);
  const cleaner = ids.filter((id) => bundle[id].role === "cleaner");
  const router = ids.filter((id) => bundle[id].role === "router");
  const fan = ids.filter((id) => !cleaner.includes(id) && !router.includes(id));
  const deps = new Map<string, string[]>();
  const fanSet = new Set(fan);
  for (const id of fan) deps.set(id, agentDeps(id, bundle[id], bundle, meta).filter((d) => fanSet.has(d)));
  const placed = new Set<string>();
  const levels: string[][] = [];
  let remaining = [...fan];
  while (remaining.length) {
    const ready = remaining.filter((id) => deps.get(id)!.every((d) => placed.has(d)));
    if (!ready.length) {
      levels.push(remaining); // cycle — shown together, executor parks them
      break;
    }
    levels.push(ready);
    ready.forEach((id) => placed.add(id));
    remaining = remaining.filter((id) => !placed.has(id));
  }
  const stages: Stage[] = [];
  if (cleaner.length) stages.push({ title: "Intake", ids: cleaner });
  if (router.length) stages.push({ title: "Route & Resolve", ids: router });
  /* Thematic level names from the prototype: the first wave comprehends the
     email, middle waves cross-check upstream outputs, the final wave emits
     work products. Falls back to numbering only to keep titles unique. */
  const levelTitle = (i: number, n: number): string => {
    if (n === 1) return "Agents";
    if (i === 0) return "Comprehend & Guard";
    if (i === n - 1) return "Work Products & Trackers";
    return n === 3 ? "Cross-checks" : `Cross-checks ${i}`;
  };
  levels.forEach((lv, i) => stages.push({ title: levelTitle(i, levels.length), ids: lv }));
  return stages;
}

export function parseReturnsFields(returns: string): Record<string, string> | null {
  try {
    const parsed = JSON.parse(returns);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>))
      out[k] = v === null ? "null" : Array.isArray(v) ? "array" : typeof v;
    return out;
  } catch {
    return null;
  }
}

export function trackerDestinations(dest: AgentDestinations | undefined): TrackerDestCfg[] {
  return [...(dest?.tracker ? [dest.tracker] : []), ...(dest?.trackers || [])];
}

function valueAtPath(value: unknown, path: string): unknown {
  let current = value;
  for (const part of path.split(".").filter(Boolean)) {
    if (!current || typeof current !== "object" || Array.isArray(current)) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/** Live requirement checks for the destinations panel. */
export function destinationIssues(def: AgentDefV2): string[] {
  const fields = parseReturnsFields(def.returns);
  const issues: string[] = [];
  if (!fields) {
    issues.push("Returns example is not valid JSON");
    return issues;
  }
  if (fields.conf !== "number") issues.push("Returns must include conf (number)");
  if (fields.out !== "string") issues.push("Returns must include out (string)");
  if (def.dest?.tray && fields.escalations !== "array")
    issues.push('Lawyer Review Tray needs "escalations" (array) in Returns');
  if (def.dest?.memo && fields.facts !== "array") issues.push('Deal Memo needs "facts" (array) in Returns');
  let returnsExample: unknown;
  try { returnsExample = JSON.parse(def.returns); } catch { returnsExample = null; }
  for (const tracker of trackerDestinations(def.dest)) {
    if (!Array.isArray(valueAtPath(returnsExample, tracker.resultKey)))
      issues.push(`Tracker needs "${tracker.resultKey}" (array) in Returns`);
  }
  for (const section of def.strictSections || []) {
    const value = valueAtPath(returnsExample, section);
    if (!value || typeof value !== "object" || Array.isArray(value))
      issues.push(`Strict section "${section}" must be an object in Returns`);
  }
  if (def.dest?.tray?.gate && !def.dest.tray.holdField) {
    let carries = false;
    try {
      const ex = JSON.parse(def.returns) as { escalations?: Array<Record<string, unknown>> };
      /* An empty example is valid for a dynamic gate: when the agent emits a
         conflict, that escalation supplies its own field at runtime. This
         mirrors the server-side validator and the builder's blank-field UX. */
      carries = Array.isArray(ex.escalations) && ex.escalations.every((e) => "field" in e);
    } catch { /* covered above */ }
    if (!carries) issues.push('A gate must name its held field (or escalations must carry "field")');
  }
  return issues;
}

export const ROLE_LABEL: Record<AgentDefV2["role"], string> = {
  cleaner: "Cleaner",
  router: "Router",
  agent: "Agent",
};

export function freshAgent(model: string): AgentDefV2 {
  return {
    v: 2,
    name: "New agent",
    desc: "",
    role: "agent",
    enabled: true,
    cond: [],
    inputs: ["email"],
    prompt: "SYSTEM:\nROLE: Describe the job.\nTASK: What to do with this email.\nOUTPUT: JSON {\"conf\": 0.0, \"out\": \"one-line summary\"}\nRULES:\n- Be precise.\n\nUSER:\nFROM: {from}\nSUBJECT: {subject}\n\nBODY:\n{delta}",
    returns: JSON.stringify({ conf: 0.0, out: "one-line summary" }, null, 2),
    dest: {},
    model,
    tau: 0.6,
    maxTokens: 512,
  };
}
